import { Receipt, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { RowListSkeleton } from '../../../../../shared/components/RowListSkeleton';
import { useOrganization } from '../../../../../shared/features/organization/components/OrganizationProvider';
import { useDispatchPermissions } from '../../../hooks/use-dispatch-permissions';
import { useTripAccessorials } from '../hooks/use-trip-accessorials';
import { DecisionModal } from './DecisionModal';
import { PanelNotice } from './PanelNotice';
import { ProposalCard } from './ProposalCard';
import { SkippedTypes } from './SkippedTypes';

/** The extra charges a closed-out trip incurred, worked out from its timeline, for the closer to confirm, adjust or drop (gitaumoses4/chukua#120). */
export function TripAccessorialsPanel({ tripId }: { tripId: string }) {
  const { state, decide } = useTripAccessorials(tripId);
  const { timezone } = useOrganization();
  const { canDecideAccessorials } = useDispatchPermissions();
  const [editing, setEditing] = useState<{ proposalKey: string; mode: 'adjusted' | 'dropped' } | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  if (state.status === 'loading') return <RowListSkeleton label="Loading accessorials" rows={2} />;
  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load the accessorials" description="Something went wrong. Try reloading the page." />;
  }

  const { accessorials } = state;
  const canDecide = canDecideAccessorials && accessorials.locked === null;
  const editingProposal = accessorials.proposals.find((proposal) => proposal.proposalKey === editing?.proposalKey);

  return (
    <section aria-label="Accessorials" className="flex flex-col gap-4">
      <div>
        <h2 className="text-base font-semibold text-fg">Accessorials</h2>
        <PanelNotice lockedReason={accessorials.locked?.reason ?? null} canDecide={canDecideAccessorials} refusal={refusal} />
      </div>
      {accessorials.proposals.length === 0 ? (
        <EmptyState icon={Receipt} title="No accessorials proposed" description="Nothing on this trip's timeline earns an extra charge on this job's terms." />
      ) : (
        accessorials.proposals.map((proposal) => (
          <ProposalCard
            key={proposal.proposalKey}
            proposal={proposal}
            timezone={timezone}
            canDecide={canDecide}
            onConfirm={() =>
              void decide({
                decision: 'confirmed',
                proposalKey: proposal.proposalKey,
                basisHash: proposal.basisHash,
                expectedDecisionId: proposal.decision?.id ?? null,
              }).then(setRefusal)
            }
            onAdjust={() => setEditing({ proposalKey: proposal.proposalKey, mode: 'adjusted' })}
            onDrop={() => setEditing({ proposalKey: proposal.proposalKey, mode: 'dropped' })}
          />
        ))
      )}
      <SkippedTypes skipped={accessorials.skipped} />
      {editing && editingProposal && <DecisionModal proposal={editingProposal} mode={editing.mode} onDecide={decide} onClose={() => setEditing(null)} />}
    </section>
  );
}
